import {
  ClientLocationControllerGetAllLocationsApiArg,
  ClientLocationControllerGetAllLocationsApiResponse,
  ClientDeliveryControllerGetOptimalDeliveryZoneApiArg,
  ClientDeliveryControllerGetOptimalDeliveryZoneApiResponse,
  ClientLocationControllerGetScheduleApiResponse,
  ClientLocationControllerGetScheduleApiArg,
  ClientOrderPointControllerFindOrderPointsApiResponse,
  ClientOrderPointControllerFindOrderPointsApiArg,
  ClientLocationControllerFindClosestLocationApiResponse,
  ClientLocationControllerFindClosestLocationApiArg,
  ClientOrderPointControllerCalculateDeliveryPriceApiResponse,
  ClientOrderPointControllerCalculateDeliveryPriceApiArg,
} from '@/app/store/api/restApi';
import { emptyApi } from '@/app/store/api/emptyApi';
import { AddressSuggestion } from '../location';
import { GeocodeArgs, SuggestArgs, YandexGeocoderResponse, YandexSuggestResponse } from './yandexTypes';

export const locationsApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getLocations: build.query<
      ClientLocationControllerGetAllLocationsApiResponse,
      ClientLocationControllerGetAllLocationsApiArg
    >({
      query: () => 'locations',
    }),

    getOptimalDeliveryZone: build.mutation<
      ClientDeliveryControllerGetOptimalDeliveryZoneApiResponse,
      ClientDeliveryControllerGetOptimalDeliveryZoneApiArg
    >({
      query: ({ getOptimalDeliveryZoneDto }) => ({
        method: 'POST',
        url: 'delivery/optimal-zone',
        body: getOptimalDeliveryZoneDto,
      }),
      invalidatesTags: ['Locations'],
    }),

    getLocationSchedule: build.mutation<
      ClientLocationControllerGetScheduleApiResponse,
      ClientLocationControllerGetScheduleApiArg
    >({
      query: ({ id, getLocationScheduleDto }) => ({
        method: 'POST',
        url: `locations/${id}/schedule`,
        body: getLocationScheduleDto,
      }),
      invalidatesTags: ['Locations'],
    }),

    getOrderPointByCity: build.mutation<
      ClientOrderPointControllerFindOrderPointsApiResponse,
      ClientOrderPointControllerFindOrderPointsApiArg
    >({
      query: (queryArg) => ({
        url: `order-points/find`,
        method: 'POST',
        body: queryArg.findOrderPointsDto,
      }),
    }),

    findClosestLocation: build.mutation<
      ClientLocationControllerFindClosestLocationApiResponse,
      ClientLocationControllerFindClosestLocationApiArg
    >({
      query: (queryArg) => ({
        url: `locations/closest`,
        method: 'POST',
        body: queryArg.findClosestLocationDto,
      }),
    }),

    calculateOrderPointDeliveryPrice: build.mutation<
      ClientOrderPointControllerCalculateDeliveryPriceApiResponse,
      ClientOrderPointControllerCalculateDeliveryPriceApiArg
    >({
      query: ({ orderPointId, clientCalculateOrderPointDeliveryPriceDto }) => ({
        url: `order-points/${orderPointId}/delivery-price`,
        method: 'POST',
        body: clientCalculateOrderPointDeliveryPriceDto,
      }),
    }),
    // Yandex geocoder via backend proxy — returns Yandex's response verbatim.
    getGeocode: build.query<YandexGeocoderResponse, GeocodeArgs>({
      query: (args) => ({
        url: 'yandex/geocode',
        params: {
          format: 'json',
          ...('geocode' in args ? { geocode: args.geocode } : { uri: args.uri }),
          ...(args.lang ? { lang: args.lang } : {}),
        },
      }),
    }),

    // Yandex address autocomplete via backend proxy — mapped to AddressSuggestion[].
    getAddressSuggestions: build.query<AddressSuggestion[], SuggestArgs>({
      query: ({ text, lang, types = 'house', ll }) => ({
        url: 'yandex/suggest/suggest',
        params: { text, lang, results: 7, attrs: 'uri', types, ...(ll ? { ll } : {}) },
      }),
      transformResponse: (response: YandexSuggestResponse): AddressSuggestion[] =>
        (response.results ?? []).map((result) => {
          const value = result.subtitle
            ? `${result.subtitle.text}, ${result.title?.text ?? ''}`
            : (result.title?.text ?? '');
          return { uri: result.uri ?? '', value, unrestricted_value: value };
        }),
    }),
  }),
});

export const {
  useGetLocationsQuery,
  useLazyGetGeocodeQuery,
  useGetOrderPointByCityMutation,
  useGetLocationScheduleMutation,
  useFindClosestLocationMutation,
  useGetOptimalDeliveryZoneMutation,
  useCalculateOrderPointDeliveryPriceMutation,
  useLazyGetAddressSuggestionsQuery,
} = locationsApi;
